// JANUS screen help (the "?" at the top of JANUS, on the list and inside a situation).
// Plain words; keep it in step with the screen in tools/JnusTool.tsx.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const jnusHelp: ScreenHelpContent = {
  title: 'JANUS',
  intro:
    'JANUS helps you make a decision and be done with it. You write down a situation, then use three tools to look at it: a slider that tells you how to decide, a scored table that compares your options, and a list of what each option could win or cost you.',
  sections: [
    {
      heading: 'Your list of situations',
      body: [
        'Press + New Situation to start one. It opens right away, called "New Situation". Rename it at the top.',
        'Each card shows the title, the date you made it and a short bit of your notes. Tap a card to open it. Press and hold for a menu: Open, Mark as Decided (or Move to Active), Delete.',
        'Decided situations move into the Archive at the bottom. Tap Archive to show or hide them.',
      ],
    },
    {
      heading: 'Inside a situation',
      body: [
        'The title is at the top. Turn on the Decided switch when you have made your choice; the situation goes to the Archive. Turn it off to reopen it.',
        'Four switches (Decision Slider, Decision Matrix, Choice S.C.O.R.E, Notes) only show or hide those parts so you can focus. They don\'t delete anything, and they all come back on each time you open a situation.',
      ],
    },
    {
      heading: 'Decision Slider',
      body: [
        'Answers "how should I decide?" Tap one square on the grid. Left to right is how much time you have: from none at all (left) to plenty (right). Bottom to top is how hard it is to undo: from easy to undo (bottom) to impossible to undo (top).',
        'The square you pick gets a name and a short piece of advice. The name is also the colour:',
      ],
      items: [
        { term: 'INSTINCT', text: 'little time, easy to undo. Go with your gut.' },
        { term: 'SWING FOR REWARD', text: 'time to spare, easy to undo. Try the boldest option.' },
        { term: 'LEAST RISKY', text: 'little time, hard to undo. Choose the safest path.' },
        { term: 'ANALYZE RISK-REWARD', text: 'time to spare, hard to undo. Weigh it up properly.' },
      ],
    },
    {
      heading: 'Decision Matrix',
      body: [
        'A table that scores your options. Rows are the things that matter (criteria), columns are your options. It starts with Cost, Quality, Time and Risk, and Option A, B and C. Tap any name to rename it.',
        'Wt is how much a criterion matters. Under each option, type a score from 1 to 10 for how well it does on that criterion. The number in brackets is weight times score. Total adds it up, and the best option is marked ★ WINNER. If two tie, there is no winner.',
        '+ Criteria and + Option add more. The × removes one (you always keep at least 1 criterion and 2 options).',
        'Weights: Flexible lets each weight be anything from 1 to 99. Locked keeps all the weights adding up to the Budget (10 to start). Raise one and the others drop to make room. The total shows green when it matches the Budget.',
      ],
    },
    {
      heading: 'Choice S.C.O.R.E',
      body: [
        'A map of the choice. Write the Situation and a Description, then the Choice you face. Press + Add Option for each way you could go.',
        'Options sit in two columns, Left and Right, and are shared out evenly as you add them. Tap ⋯ on an option for Add Reward, Add Exposure, Move to the other side, or Delete Option.',
      ],
      items: [
        { term: 'Reward', text: 'something you gain if you pick this option.' },
        { term: 'Exposure', text: 'something you risk or lose if you pick it.' },
      ],
    },
    {
      heading: 'Notes and saving',
      body: [
        'Notes is free space for anything else.',
        'There is no Save button. Everything saves by itself a moment after you stop typing or tapping, and the slider square and matrix numbers are remembered too. The Decided switch and the list menu save straight away.',
      ],
    },
  ],
};
